import { useCallback } from 'react';
import { useRouter } from 'expo-router';

import { useTelemetry } from '@order/Telemetry';

// ─────────────────────────────────────────────────────────────────────────────

export const useQuickAddNavigation = (params: Params) => {
  const {
    baseProductSlug,
    productSlug,
    restaurantSlug,
    selectedDeliveryAddressId,
    sharedLineItemId,
  } = params;

  const router = useRouter();
  const { track } = useTelemetry();

  // ─── Handlers ────────────────────────────────────────────────────────

  const navigateToLocationSearch = useCallback(() => {
    track('quick-add.change-location-tapped', { baseProductSlug, sharedLineItemId });

    router.navigate({
      pathname: '/quick-add/[baseProductSlug]/search-location',
      params: { baseProductSlug },
    });
  }, [baseProductSlug, router, sharedLineItemId, track]);

  const navigateToCustomization = useCallback(() => {
    if (!productSlug || !restaurantSlug) {
      navigateToLocationSearch();

      return;
    }

    track('quick-add.customize-tapped', { baseProductSlug, sharedLineItemId });

    const sharedLineItemParams = sharedLineItemId ? { sharedLineItemId } : {};

    if (selectedDeliveryAddressId) {
      router.replace({
        pathname: '/delivery/[addressId]/[productSlug]',
        params: { addressId: selectedDeliveryAddressId, productSlug, ...sharedLineItemParams },
      });

      return;
    }

    router.replace({
      pathname: '/[restaurantSlug]/[productSlug]',
      params: { restaurantSlug, productSlug, ...sharedLineItemParams },
    });
  }, [
    baseProductSlug,
    navigateToLocationSearch,
    productSlug,
    restaurantSlug,
    router,
    selectedDeliveryAddressId,
    sharedLineItemId,
    track,
  ]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    navigateToCustomization,
    navigateToLocationSearch,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  baseProductSlug: string;
  productSlug?: string;
  restaurantSlug?: string;
  selectedDeliveryAddressId?: string;
  sharedLineItemId?: string;
};
